import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Download } from 'lucide-react';
import LogoLoader from '@/components/LogoLoader';
import { Skeleton } from '@/components/ui/skeleton';

const normalizeArrayResponse = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

const Ledger = () => {
  const { token } = useAuth();
  const [ledgerScope, setLedgerScope] = useState('client');
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedConsultantId, setSelectedConsultantId] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [entries, setEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [vendorInvoices, setVendorInvoices] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const loadParticipants = async () => {
      setLoadingEntities(true);
      try {
        const [clientsResponse, consultantsResponse, vendorsResponse] = await Promise.all([
          api.get('/clients', token),
          api.get('/consultants', token),
          api.get('/vendors', token)
        ]);

        const normalizedClients = normalizeArrayResponse(clientsResponse);
        const normalizedConsultants = normalizeArrayResponse(consultantsResponse);
        const normalizedVendors = normalizeArrayResponse(vendorsResponse);

        setClients(normalizedClients);
        setConsultants(normalizedConsultants);
        setVendors(normalizedVendors);

        if (normalizedClients.length > 0) {
          setSelectedClientId((prev) => prev || normalizedClients[0].id);
        }
        if (normalizedConsultants.length > 0) {
          setSelectedConsultantId((prev) => prev || normalizedConsultants[0].id);
        }
        if (normalizedVendors.length > 0) {
          setSelectedVendorId((prev) => prev || String(normalizedVendors[0].id));
        }
      } catch (error) {
        console.error('Failed to load ledger participants', error);
        toast.error('Failed to load ledger participants');
      } finally {
        setLoadingEntities(false);
      }
    };

    loadParticipants();
  }, [token]);

  useEffect(() => {
    if (ledgerScope === 'client') {
      if (!selectedClientId && clients.length > 0) {
        setSelectedClientId(clients[0].id);
      }
    } else if (ledgerScope === 'consultant') {
      if (!selectedConsultantId && consultants.length > 0) {
        setSelectedConsultantId(consultants[0].id);
      }
    } else if (ledgerScope === 'vendor') {
      if (!selectedVendorId && vendors.length > 0) {
        setSelectedVendorId(String(vendors[0].id));
      }
    }
  }, [ledgerScope, clients, consultants, vendors, selectedClientId, selectedConsultantId, selectedVendorId]);

  useEffect(() => {
    const fetchEntries = async () => {
      const scopeId =
        ledgerScope === 'client'
          ? selectedClientId
          : ledgerScope === 'consultant'
            ? selectedConsultantId
            : selectedVendorId;
      if (!scopeId) {
        setEntries([]);
        return;
      }

      setLoadingEntries(true);
      try {
        const queryParam =
          ledgerScope === 'client'
            ? `client_id=${scopeId}`
            : ledgerScope === 'consultant'
              ? `consultant_id=${scopeId}`
              : `vendor_id=${scopeId}`;
        const data = await api.get(`/accounting/ledgers?${queryParam}`, token);
        setEntries(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load ledger entries', error);
        toast.error('Failed to load ledger entries');
      } finally {
        setLoadingEntries(false);
      }
    };

    fetchEntries();
  }, [ledgerScope, selectedClientId, selectedConsultantId, selectedVendorId, token]);

  useEffect(() => {
    const loadLedgerMetadata = async () => {
      if (!token) return;
      try {
        const [
          projectsData,
          purchaseOrdersData,
          invoicesData,
          receiptsData,
          timeEntriesData,
          vendorInvoicesData
        ] = await Promise.all([
          api.get('/projects', token),
          api.get('/purchase-orders', token),
          api.get('/invoices', token),
          api.get('/receipts', token),
          api.get('/time-entries', token),
          api.get('/vendor-invoices', token)
        ]);
        setProjects(normalizeArrayResponse(projectsData));
        setPurchaseOrders(normalizeArrayResponse(purchaseOrdersData));
        setInvoices(normalizeArrayResponse(invoicesData));
        setReceipts(normalizeArrayResponse(receiptsData));
        setTimeEntries(normalizeArrayResponse(timeEntriesData));
        setVendorInvoices(normalizeArrayResponse(vendorInvoicesData));
      } catch (error) {
        console.error('Failed to load ledger metadata', error);
        toast.error('Failed to load ledger metadata');
      }
    };

    loadLedgerMetadata();
  }, [token]);

  const invoiceMap = useMemo(() => {
    const map = new Map();
    invoices.forEach((invoice) => {
      if (invoice?.id) {
        map.set(invoice.id, invoice);
      }
    });
    return map;
  }, [invoices]);

  const receiptMap = useMemo(() => {
    const map = new Map();
    receipts.forEach((receipt) => {
      if (receipt?.id) {
        map.set(receipt.id, receipt);
      }
    });
    return map;
  }, [receipts]);

  const projectMap = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => {
      if (project?.id) {
        map.set(project.id, project);
      }
    });
    return map;
  }, [projects]);

  const purchaseOrderMap = useMemo(() => {
    const map = new Map();
    purchaseOrders.forEach((po) => {
      if (po?.id) {
        map.set(po.id, po);
      }
    });
    return map;
  }, [purchaseOrders]);

  const timeEntryMap = useMemo(() => {
    const map = new Map();
    timeEntries.forEach((entry) => {
      if (entry?.id) {
        map.set(entry.id, entry);
      }
    });
    return map;
  }, [timeEntries]);

  const clientMap = useMemo(() => {
    const map = new Map();
    clients.forEach((client) => {
      if (client?.id) {
        map.set(client.id, client);
      }
    });
    return map;
  }, [clients]);

  const consultantMap = useMemo(() => {
    const map = new Map();
    consultants.forEach((consultant) => {
      if (consultant?.id) {
        map.set(consultant.id, consultant);
      }
    });
    return map;
  }, [consultants]);

  const vendorInvoiceMap = useMemo(() => {
    const map = new Map();
    vendorInvoices.forEach((invoice) => {
      if (invoice?.id) {
        map.set(invoice.id, invoice);
      }
    });
    return map;
  }, [vendorInvoices]);

  const formatDisplayDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString();
  };

  const buildInvoicePeriodLabel = (invoice) => {
    if (!invoice) return '';
    if (invoice.period_label) {
      return invoice.period_label;
    }

    const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
    if (lines.length === 0) {
      return '';
    }

    const startDates = lines
      .map((line) => line?.period_start)
      .filter(Boolean)
      .sort();
    const endDates = lines
      .map((line) => line?.period_end)
      .filter(Boolean)
      .sort();

    const start = startDates.length > 0 ? startDates[0] : null;
    const end = endDates.length > 0 ? endDates[endDates.length - 1] : null;

    if (start && end) {
      return `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`;
    }
    if (start) {
      return formatDisplayDate(start);
    }
    if (end) {
      return formatDisplayDate(end);
    }
    return '';
  };

  const handleExport = async (format = 'csv') => {
    const scopeId =
      ledgerScope === 'client'
        ? selectedClientId
        : ledgerScope === 'consultant'
          ? selectedConsultantId
          : selectedVendorId;
    if (!scopeId) {
      toast.error('Select a ledger participant before exporting');
      return;
    }

    setExporting(true);
    try {
      const queryParam =
        ledgerScope === 'client'
          ? `client_id=${scopeId}`
          : ledgerScope === 'consultant'
            ? `consultant_id=${scopeId}`
            : `vendor_id=${scopeId}`;
      const response = await api.get(
        `/accounting/ledgers/export?${queryParam}&format=${format}`,
        token,
        { responseType: 'blob' }
      );

      const blob = new Blob([
        response
      ], {
        type:
          format === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const scopeLabel =
        ledgerScope === 'client' ? 'client' : ledgerScope === 'consultant' ? 'consultant' : 'vendor';
      const filenameSuffix = format === 'xlsx' ? 'xlsx' : 'csv';
      link.href = url;
      link.download = `${scopeLabel}-ledger-${scopeId}.${filenameSuffix}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ledger exported successfully');
    } catch (error) {
      console.error('Failed to export ledger', error);
      toast.error('Failed to export ledger');
    } finally {
      setExporting(false);
    }
  };

  const selectedEntity = useMemo(() => {
    if (ledgerScope === 'client') {
      return clients.find((client) => client.id === selectedClientId);
    }
    if (ledgerScope === 'consultant') {
      return consultants.find((consultant) => consultant.id === selectedConsultantId);
    }
    return vendors.find((vendor) => String(vendor.id) === selectedVendorId);
  }, [ledgerScope, clients, consultants, vendors, selectedClientId, selectedConsultantId, selectedVendorId]);

  const formatCurrency = (value) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value || 0);
    } catch (error) {
      return `USD ${Number(value || 0).toFixed(2)}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString();
  };

  const ledgerRows = useMemo(() => {
    let runningBalance = 0;
    return entries.map((entry) => {
      const amount = Number(entry.amount || 0);
      const isDebit = entry.entry_type === 'debit';
      const debit = isDebit ? amount : 0;
      const credit = isDebit ? 0 : amount;
      runningBalance += debit - credit;
      let reference = entry.invoice_id || entry.payment_id || entry.time_entry_id || entry.entry_id;
      let descriptionDetails = entry.memo || '';

      if (ledgerScope === 'client') {
        const invoice = entry.invoice_id ? invoiceMap.get(entry.invoice_id) : undefined;
        const poNumber = invoice?.po_id ? purchaseOrderMap.get(invoice.po_id)?.po_number || invoice.po_id : undefined;
        const projectCode = invoice?.project_id
          ? projectMap.get(invoice.project_id)?.code || projectMap.get(invoice.project_id)?.name || invoice.project_id
          : undefined;
        const periodLabel = buildInvoicePeriodLabel(invoice);
        const descriptorParts = [];
        if (poNumber) {
          descriptorParts.push(`PO: ${poNumber}`);
        } else if (projectCode) {
          descriptorParts.push(`Project: ${projectCode}`);
        }
        if (periodLabel) {
          descriptorParts.push(`Period: ${periodLabel}`);
        }
        if (descriptorParts.length > 0) {
          descriptionDetails = descriptorParts.join(' | ');
        }

        if (entry.payment_id) {
          const receiptId = entry.payment_id.split(':')[0];
          const receipt = receiptMap.get(receiptId);
          if (receipt?.reference) {
            reference = receipt.reference;
          } else if (invoice?.invoice_no) {
            reference = invoice.invoice_no;
          } else if (invoice?.invoice_number) {
            reference = invoice.invoice_number;
          } else if (!reference && invoice?.id) {
            reference = invoice.id;
          }
        } else if (invoice) {
          if (invoice.invoice_no) {
            reference = invoice.invoice_no;
          } else if (invoice.invoice_number) {
            reference = invoice.invoice_number;
          } else if (!reference && invoice.id) {
            reference = invoice.id;
          }
        }
      } else if (ledgerScope === 'consultant') {
        const timeEntry = entry.time_entry_id ? timeEntryMap.get(entry.time_entry_id) : undefined;
        const assignment = (() => {
          if (timeEntry?.po_id) {
            return purchaseOrderMap.get(timeEntry.po_id)?.po_number || timeEntry.po_id;
          }
          if (timeEntry?.project_id) {
            const project = projectMap.get(timeEntry.project_id);
            return project?.code || project?.name || timeEntry.project_id;
          }
          return undefined;
        })();
        const clientName = timeEntry?.client_id
          ? clientMap.get(timeEntry.client_id)?.name || clientMap.get(timeEntry.client_id)?.legal_name || timeEntry.client_id
          : undefined;

        if (assignment || clientName) {
          const parts = [];
          if (assignment) {
            parts.push(assignment);
          }
          if (clientName) {
            parts.push(clientName);
          }
          reference = parts.join(' • ');
        }
        descriptionDetails = entry.memo || '';
      } else if (ledgerScope === 'vendor') {
        const vendorInvoice = entry.invoice_id ? vendorInvoiceMap.get(entry.invoice_id) : undefined;
        const timeEntry = entry.time_entry_id ? timeEntryMap.get(entry.time_entry_id) : undefined;
        if (vendorInvoice) {
          reference = vendorInvoice.invoice_no || vendorInvoice.id || reference;
          const descriptorParts = [];
          if (vendorInvoice.invoice_date) {
            descriptorParts.push(`Invoice Date: ${formatDisplayDate(vendorInvoice.invoice_date)}`);
          }
          if (vendorInvoice.due_date) {
            descriptorParts.push(`Due: ${formatDisplayDate(vendorInvoice.due_date)}`);
          }
          descriptionDetails = descriptorParts.join(' | ') || entry.memo || '';
        } else if (entry.payment_id) {
          reference = entry.payment_id || reference;
          descriptionDetails = entry.memo || '';
        } else if (timeEntry) {
          const assignment = (() => {
            if (timeEntry.po_id) {
              return purchaseOrderMap.get(timeEntry.po_id)?.po_number || timeEntry.po_id;
            }
            if (timeEntry.project_id) {
              const project = projectMap.get(timeEntry.project_id);
              return project?.code || project?.name || timeEntry.project_id;
            }
            return undefined;
          })();
          const consultant = timeEntry.consultant_id ? consultantMap.get(timeEntry.consultant_id) : undefined;
          const clientName = timeEntry.client_id
            ? clientMap.get(timeEntry.client_id)?.name || clientMap.get(timeEntry.client_id)?.legal_name || timeEntry.client_id
            : undefined;
          const parts = [];
          if (assignment) {
            parts.push(assignment);
          }
          if (consultant?.name) {
            parts.push(consultant.name);
          }
          if (clientName) {
            parts.push(clientName);
          }
          if (parts.length > 0) {
            reference = parts.join(' • ');
          }
          descriptionDetails = entry.memo || '';
        } else {
          descriptionDetails = entry.memo || '';
        }
        if (!reference && entry.invoice_id) {
          reference = entry.invoice_id;
        }
        if (!reference && entry.time_entry_id) {
          reference = entry.time_entry_id;
        }
      }

      return {
        ...entry,
        debit,
        credit,
        balance: runningBalance,
        reference,
        description: descriptionDetails
      };
    });
  }, [
    entries,
    ledgerScope,
    invoiceMap,
    purchaseOrderMap,
    projectMap,
    receiptMap,
    timeEntryMap,
    clientMap,
    consultantMap,
    vendorInvoiceMap
  ]);

  const debitTotal = ledgerRows.reduce((sum, entry) => sum + entry.debit, 0);
  const creditTotal = ledgerRows.reduce((sum, entry) => sum + entry.credit, 0);
  const balance = ledgerRows.length > 0 ? ledgerRows[ledgerRows.length - 1].balance : 0;

  if (loadingEntities) {
    return <LogoLoader message="Loading ledger..." />;
  }

  const noClients = ledgerScope === 'client' && clients.length === 0;
  const noConsultants = ledgerScope === 'consultant' && consultants.length === 0;
  const noVendors = ledgerScope === 'vendor' && vendors.length === 0;

  if (noClients || noConsultants || noVendors) {
    const label = ledgerScope === 'client' ? 'clients' : ledgerScope === 'consultant' ? 'consultants' : 'vendors';
    return (
      <div className="space-y-6" data-testid="ledger-page">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Ledger</h1>
          <p className="text-gray-600 mt-1">Track financial activity by client, consultant, or vendor</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            Add {label} to begin tracking ledger activity.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Ledger</h1>
          <p className="text-gray-600 mt-1">Track financial activity by client, consultant, or vendor</p>
        </div>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="md:w-48">
            <Select value={ledgerScope} onValueChange={setLedgerScope}>
              <SelectTrigger>
                <SelectValue placeholder="Select ledger type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client Ledger</SelectItem>
                <SelectItem value="consultant">Consultant Ledger</SelectItem>
                <SelectItem value="vendor">Vendor Ledger</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:w-72">
            {ledgerScope === 'client' ? (
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name || client.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : ledgerScope === 'consultant' ? (
              <Select value={selectedConsultantId} onValueChange={setSelectedConsultantId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select consultant" />
                </SelectTrigger>
                <SelectContent>
                  {consultants.map((consultant) => (
                    <SelectItem key={consultant.id} value={consultant.id}>
                      {consultant.name || consultant.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={selectedVendorId} onValueChange={setSelectedVendorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={String(vendor.id)}>
                      {vendor.name || vendor.legal_name || String(vendor.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={
                  exporting ||
                  (!selectedClientId && ledgerScope === 'client') ||
                  (!selectedConsultantId && ledgerScope === 'consultant') ||
                  (!selectedVendorId && ledgerScope === 'vendor')
                }
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting…' : 'Export Ledger'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault();
                  handleExport('csv');
                }}
              >
                Download CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault();
                  handleExport('xlsx');
                }}
              >
                Download Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ledger Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div>
            <div className="text-sm text-gray-500">
              {ledgerScope === 'client' ? 'Client' : ledgerScope === 'consultant' ? 'Consultant' : 'Vendor'}
            </div>
            <div className="text-xl font-semibold text-gray-900">
              {ledgerScope === 'vendor'
                ? selectedEntity?.name || selectedEntity?.legal_name || '—'
                : selectedEntity?.name || '—'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Debits</div>
            <div className="text-xl font-semibold text-gray-900">{formatCurrency(debitTotal)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Credits</div>
            <div className="text-xl font-semibold text-gray-900">{formatCurrency(creditTotal)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Balance</div>
            <div className={`text-xl font-semibold ${balance > 0 ? 'text-utis-forest' : 'text-gray-900'}`}>
              {formatCurrency(balance)}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ledger Activity</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {loadingEntries ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : ledgerRows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No ledger entries yet. Record transactions to see activity.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Transaction Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Debit</TableHead>
                  <TableHead>Credit</TableHead>
                  <TableHead>Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgerRows.map((entry) => (
                  <TableRow key={entry.entry_id}>
                    <TableCell>{formatDate(entry.occurred_at)}</TableCell>
                    <TableCell className="font-medium">{entry.reference || '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant={entry.entry_type === 'debit' ? 'default' : 'secondary'} className="capitalize w-fit">
                          {entry.entry_type}
                        </Badge>
                        <div className="text-sm text-gray-600">
                          {entry.description || entry.memo || '—'}
                          {entry.description && entry.memo && entry.memo !== entry.description ? (
                            <span className="text-xs text-gray-500">{entry.memo}</span>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(entry.debit)}</TableCell>
                    <TableCell>{formatCurrency(entry.credit)}</TableCell>
                    <TableCell className="font-semibold">{formatCurrency(entry.balance)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-gray-50 font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell colSpan={2}>
                    <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                      <span>Entries: {ledgerRows.length}</span>
                      <span>Last updated: {formatDate(ledgerRows[ledgerRows.length - 1]?.occurred_at)}</span>
                    </div>
                  </TableCell>
                  <TableCell>{formatCurrency(debitTotal)}</TableCell>
                  <TableCell>{formatCurrency(creditTotal)}</TableCell>
                  <TableCell>{formatCurrency(balance)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Ledger;
